import React, { useEffect, useState, useContext, useRef, memo } from "react";

// Libraries
import * as Yup from "yup";
import PropTypes from "prop-types";
import { Formik, Form } from "formik";
import { useScroll } from "framer-motion";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Container, Navbar } from "react-bootstrap";
import useOnClickOutside from "../../Functions/UseOnClickOutside";
import { useTranslation } from "react-i18next";
import { detectLanguageFromPath, DEFAULT_LANGUAGE } from "../../utils/languageUtils";

// Component
import { Input } from "../Form";
import Buttons from "../Button/Buttons";
import ReactCustomScrollbar from "../ReactCustomScrollbar";
import LanguageSwitcher from "../LanguageSwitcher/LanguageSwitcher";

// Context
import GlobalContext from "../../Context/Context";

// Data
import HeaderData from "./HeaderData";

// css
import "../../Assets/scss/layouts/_header.scss";
import "./_header-override.scss";

/* Header Component Start */
export const Header = memo((props) => {
  // Add Global Header Data
  const { setHeaderHeight } = useContext(GlobalContext);
  const { scrollY } = useScroll();
  const [scrollPos, setScrollPos] = useState({
    y: 0,
    prevY: -1,
    directionDown: true,
  });
  const location = useLocation();

  const handleProgrammaticScroll = () => {
    // After a programmatic scroll, update the scroll position state
    // to reflect that we've scrolled down
    setScrollPos({
      y: window.scrollY,
      prevY: scrollPos.y,
      directionDown: true,
    });
  };

  // Pass this function to the context so it can be used by menu items
  useEffect(() => {
    // You need to add this to your GlobalContext if not already there
    if (props.setProgrammaticScrollHandler) {
      props.setProgrammaticScrollHandler(handleProgrammaticScroll);
    }
  }, [props, scrollPos.y]);

  useEffect(() => {
    let headerEl = document.querySelector("header");

    // Calculate header height
    function setTopSpace() {
      let windowWidth = window.innerWidth,
        headerheight =
          props.topSpace.desktop && props.topSpace.desktop === true
            ? headerEl.offsetHeight
            : 0;

      if (windowWidth <= 1199 && props.topSpace.lg) {
        headerheight = props.topSpace.lg === true ? headerEl.offsetHeight : 0;
      }

      if (windowWidth <= 991 && props.topSpace.md) {
        headerheight = props.topSpace.md === true ? headerEl.offsetHeight : 0;
      }

      if (windowWidth <= 767 && props.topSpace.sm) {
        headerheight = props.topSpace.sm === true ? headerEl.offsetHeight : 0;
      }

      if (windowWidth <= 575 && props.topSpace.xs) {
        headerheight = props.topSpace.xs === true ? headerEl.offsetHeight : 0;
      }

      setHeaderHeight(headerheight);
    }

    setTopSpace();

    window.addEventListener("load", setTopSpace);
    window.addEventListener("resize", setTopSpace);

    if (document.body.classList.contains("mobile-menu")) {
      document.body.classList.remove("menu-modern");
      document.body.classList.remove("menu-full");
      document.body.style.removeProperty("overflow");
    }
    // Return cleanup function
    return () => {
      window.removeEventListener("load", setTopSpace);
      window.removeEventListener("resize", setTopSpace);
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location]);

  useEffect(() => {
    let lastScrollTop = 0;
    // Store the unsubscribe function from scrollY.onChange
    const unsubscribeScroll = scrollY.onChange((pos) => {
      if (pos > lastScrollTop) {
        setScrollPos({
          ...scrollPos,
          y: pos,
          prevY: pos - 1,
          directionDown: true,
        });
      } else {
        setScrollPos({
          ...scrollPos,
          y: pos,
          prevY: pos - 1,
          directionDown: false,
        });
      }

      if (pos === 0) {
        setScrollPos({ ...scrollPos, directionDown: true });
      }
      lastScrollTop = pos;
    });

    // Return a cleanup function that unsubscribes when component unmounts
    return () => {
      if (unsubscribeScroll) {
        unsubscribeScroll();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <header
      className={`${props.className ? props.className : ""}${
        scrollPos.y > 5 ? " sticky-header" : ""
      }${scrollPos.directionDown === false ? " header-appear" : ""}${
        scrollPos.directionDown === true && scrollPos.y > 50
          ? " header-disappear"
          : ""
      }${props.type ? ` ${props.type}` : ""}`}
    >
      {props.children}
    </header>
  );
});
/* Header Component End */

/* Headernav Component Start */
export const HeaderNav = (props) => {
  // Add state to track menu visibility
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  // Modified handler that updates state
  const handleMenuToggle = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  // Effect to sync state changes with DOM
  useEffect(() => {
    const navbarCollapse = document.querySelector(".navbar-collapse");
    const toggleButton = document.querySelector(".navbar-toggler");

    if (isMenuOpen) {
      document.body.classList.add("navbar-collapse-show");
      navbarCollapse.classList.add("show");
      toggleButton.classList.remove("collapsed");
      toggleButton.setAttribute("aria-expanded", "true");
    } else {
      document.body.classList.remove("navbar-collapse-show");
      navbarCollapse.classList.remove("show");
      toggleButton.classList.add("collapsed");
      toggleButton.setAttribute("aria-expanded", "false");

      // Close any open menu items
      const menuItems = document.querySelectorAll(".nav-item");
      menuItems.forEach(function (item) {
        if (item.classList.contains("open")) {
          setTimeout(() => {
            item.classList.remove("open");
          }, 200);
        }
      });
    }
  }, [isMenuOpen]);

  // Add event listener for navbar link clicks
  useEffect(() => {
    const handleNavbarLinkClick = (e) => {
      // Check if clicked element is a nav-link
      if (e.target.matches(".nav-link") || e.target.closest(".nav-link")) {
        // Update the React state to close the navbar
        setIsMenuOpen(false);
      }
    };

    // Add the event listener to the document
    document.addEventListener("click", handleNavbarLinkClick);

    // Cleanup function to remove event listener
    return () => {
      document.removeEventListener("click", handleNavbarLinkClick);
    };
  }, []);

  return (
    <Navbar
      collapseOnSelect
      id='headerbar'
      expand={props.expand}
      bg={props.bg ? props.bg : "transparent"}
      variant={props.theme}
      className={`${props.menu && `menu-${props.menu}`}${
        props.className ? ` ${props.className}` : ""
      }${props.bg || props.bg === "transparent" ? "" : " header-transparent"}`}
      onToggle={handleMenuToggle}
      expanded={isMenuOpen}
    >
      <Container
        fluid={props.fluid}
        className={props.containerClass ? props.containerClass : ""}
        style={props.containerStyle ? props.containerStyle : {}}
      >
        {props.children}
      </Container>
    </Navbar>
  );
};
/* Headernav Component End */

/* Menu Component Start */
export const Menu = memo((props) => {
  const megamenu_ref = useRef(null);
  const [isMenuActive, setMenuActive] = useState(null);
  const [isHover, setIsHover] = useState(false);
  // Add state for hover image at component level
  const [currentHoverImage, setCurrentHoverImage] = useState(null);

  const handleMenuClick = (e, index) =>
    setMenuActive(index !== isMenuActive ? index : null);

  // set Active Menu
  const location = useLocation();

  // Add this to handle modal opening
  const handleMenuItemClick = (item) => {
    // Check if the item has a modal action
    if (item.modalAction === "openContactModal" && props.openModal) {
      // Call the provided openModal function
      props.openModal();
      return true; // Return true to indicate the action was handled
    }
    return false; // Return false to allow normal navigation
  };

  useEffect(() => {
    let header = document.querySelector("header"),
      navItem = header.querySelectorAll(".nav-item"),
      links = header.querySelectorAll(".nav-link");

    navItem.forEach((item) => item.classList.remove("active"));

    const activeLink = Array.from(links).find(
      (link) => link.getAttribute("href") === location.pathname
    );
    if (activeLink) {
      import("../../Functions/Utilities").then((module) => {
        let filtered_dropdown = module
          .getParents(activeLink)
          .filter((item) => item.classList.contains("simple-dropdown"));
        let filtered_nav_item = module
          .getParents(activeLink)
          .filter((item) => item.classList.contains("nav-item"));
        filtered_dropdown.forEach((item) => item.classList.add("active"));
        filtered_nav_item.forEach((item) => item.classList.add("active"));
      });
    }
  }, [location, isHover]);

  useEffect(() => {
    let navItems = document
      .querySelector("header")
      .querySelectorAll(".navbar-nav > .nav-item");
    navItems.forEach((nav) =>
      nav.addEventListener("mouseover", () => setIsHover(true))
    );
  }, []);

  return (
    <div
      className={`${props.mobileMenu ? `mobile-menu-${props.mobileMenu}` : ""}${
        props.className ? ` ${props.className}` : ""
      }`}
    >
      <ul className='navbar-nav'>
        {props.data.map((item, i) => {
          // Get default image for this megamenu if present
          const defaultImage = item.megamenu
            ? item.megamenu.find((column) => column.defaultImage)
                ?.defaultImage || ""
            : "";

          return (
            <li
              className={`nav-item${
                item.dropdown || item.megamenu ? ` dropdown` : ""
              }${isMenuActive === i ? " open" : ""}`}
              key={i}
            >
              {item.link ? (
                item.external ? (
                  <a
                    className={`nav-link ${item.className || ""}`}
                    href={item.link}
                    target='_blank'
                    rel='noopener noreferrer'
                  >
                    {item.title}
                    {item.notification && (
                      <span
                        className={`notification-dot absolute top-[1.5rem] right-[-1.2rem] bg-red-500 text-white rounded-full w-[14px] h-[14px] flex items-center justify-center text-[9px] ${
                          item.notification.animate ? "" : "no-animate"
                        }`}
                      >
                        {item.notification.count}
                      </span>
                    )}
                  </a>
                ) : (
                  <Link
                    className={`nav-link ${item.className || ""}`}
                    to={item.link}
                    onClick={(e) => {
                      // Check if this is a modal action item
                      if (item.modalAction && handleMenuItemClick(item)) {
                        e.preventDefault(); // Prevent default navigation
                      } else if (item.onClick) {
                        e.preventDefault();
                        item.onClick();
                      }
                    }}
                  >
                    {item.title}
                    {item.notification && (
                      <span
                        className={`notification-dot absolute top-[1.5rem] right-[-1.2rem] bg-red-500 text-white rounded-full w-[14px] h-[14px] flex items-center justify-center text-[9px] ${
                          item.notification.animate ? "" : "no-animate"
                        }`}
                      >
                        {item.notification.count}
                      </span>
                    )}
                  </Link>
                )
              ) : item.modalAction ? (
                // Special handling for items with modalAction but no link
                <span
                  className={`nav-link cursor-pointer ${item.className || ""}`}
                  onClick={() => handleMenuItemClick(item)}
                >
                  {item.title}
                  {item.notification && (
                    <span
                      className={`notification-dot absolute top-[1.5rem] right-[-1.2rem] bg-red-500 text-white rounded-full w-[14px] h-[14px] flex items-center justify-center text-[9px] ${
                        item.notification.animate ? "" : "no-animate"
                      }`}
                    >
                      {item.notification.count}
                    </span>
                  )}
                </span>
              ) : (
                <span className={`nav-link ${item.className || ""}`}>
                  {item.title}
                </span>
              )}
              {/* Remove the arrow/toggle from desktop menu - No arrow in desktop menu */}
              {item.dropdown && (
                <ul className='simple-dropdown-menu'>
                  {item.dropdown.map((item, i) => {
                    return (
                      <li key={i} className='simple-dropdown'>
                        {item.link ? (
                          <Link className='nav-link' to={item.link}>
                            {item.title}
                            {item.dropdown && (
                              <i className='fas fa-angle-right'></i>
                            )}
                          </Link>
                        ) : (
                          <span className='nav-link'>
                            {item.title}
                            {item.dropdown && (
                              <i className='fas fa-angle-right'></i>
                            )}
                          </span>
                        )}
                        {item.dropdown && (
                          <ul className='simple-dropdown-menu'>
                            {item.dropdown.map((item, i) => {
                              return (
                                <li key={i} className='simple-dropdown'>
                                  {item.link ? (
                                    <Link
                                      className={`nav-link${
                                        item.dropdown
                                          ? " md:text-black md:mt-[15px] md:mb-[7px]"
                                          : ""
                                      }`}
                                      to={item.link}
                                    >
                                      {item.title}
                                      {item.dropdown && (
                                        <i className='fas fa-angle-right'></i>
                                      )}
                                    </Link>
                                  ) : (
                                    <span className='nav-link'>
                                      {item.title}
                                      {item.dropdown && (
                                        <i className='fas fa-angle-right'></i>
                                      )}
                                    </span>
                                  )}
                                  {item.dropdown && (
                                    <ul className='simple-dropdown-menu'>
                                      {item.dropdown.map((item, i) => {
                                        return (
                                          <li
                                            className='simple-dropdown'
                                            key={i}
                                          >
                                            <Link
                                              className='nav-link'
                                              to={item.link}
                                            >
                                              {item.title}
                                            </Link>
                                          </li>
                                        );
                                      })}
                                    </ul>
                                  )}
                                </li>
                              );
                            })}
                          </ul>
                        )}
                      </li>
                    );
                  })}
                </ul>
              )}
              {item.megamenu && (
                <div
                  className={`flex megamenu ${item.megamenuClassName || ""}`}
                  ref={megamenu_ref}
                  style={{
                    display: "flex",
                    flexWrap: "nowrap",
                    alignItems: "flex-start",
                  }}
                  onMouseEnter={() => {
                    // When entering the megamenu, only set the default image if no image has been hovered yet
                    if (
                      defaultImage &&
                      item.megamenuClassName === "services-megamenu" &&
                      !currentHoverImage
                    ) {
                      setCurrentHoverImage(defaultImage);
                    }
                  }}
                  onMouseLeave={() => {
                    // Optionally reset to default when leaving the entire megamenu
                    // Comment this out if you want to keep the last image even after leaving the menu
                    // if (defaultImage && item.megamenuClassName === "services-megamenu") {
                    //   setCurrentHoverImage(defaultImage);
                    // }
                  }}
                >
                  {item.megamenu.map((column, i) => {
                    return (
                      <ul
                        className={`${
                          column.dropdown.filter((item) => item.img).length > 0
                            ? "!pr-[30px] img-wrapper inline-block last:!pr-[0px]"
                            : "inline-block"
                        }`}
                        key={i}
                      >
                        {column.title && (
                          <li className='title text-md font-medium mb-[15px] whitespace-nowrap'>
                            {column.title}
                          </li>
                        )}
                        {column.dropdown &&
                          column.dropdown.map((dropItem, j) => {
                            return (
                              <li
                                className='nav-item'
                                key={j}
                                onMouseEnter={() => {
                                  if (
                                    dropItem.hoverImage &&
                                    item.megamenuClassName ===
                                      "services-megamenu"
                                  ) {
                                    setCurrentHoverImage(dropItem.hoverImage);
                                  }
                                }}
                                // Remove the onMouseLeave handler so the image doesn't revert
                              >
                                {dropItem.title &&
                                  (dropItem.isClickable === false ? (
                                    <span className='nav-link dropdown-item-non-clickable'>
                                      {dropItem.icon && (
                                        <i
                                          className={`${dropItem.icon} mr-[10px]`}
                                        ></i>
                                      )}
                                      {dropItem.title}
                                      {dropItem.subtitle && (
                                        <span className='block mt-2 md:mt-4 text-sm'>
                                          {dropItem.subtitle}
                                        </span>
                                      )}
                                    </span>
                                  ) : dropItem.modalAction ? (
                                    // Special handling for items with modalAction
                                    <span
                                      className='nav-link cursor-pointer'
                                      onClick={() => {
                                        if (props.openModal) props.openModal();
                                      }}
                                    >
                                      {dropItem.icon && (
                                        <i
                                          className={`${dropItem.icon} mr-[10px]`}
                                        ></i>
                                      )}
                                      {dropItem.title}
                                      {dropItem.subtitle && (
                                        <span className='block mt-2 md:mt-4 text-sm'>
                                          {dropItem.subtitle}
                                        </span>
                                      )}
                                    </span>
                                  ) : (
                                    <Link
                                      className='nav-link'
                                      to={dropItem.link ? dropItem.link : "#"}
                                      onClick={(e) => {
                                        if (dropItem.onClick) {
                                          e.preventDefault();
                                          dropItem.onClick();
                                        } else if (dropItem.scrollTo) {
                                          e.preventDefault();
                                          const sectionId = dropItem.scrollTo;
                                          // Scroll to section if on the same page
                                          const element =
                                            document.getElementById(sectionId);
                                          if (element) {
                                            element.scrollIntoView({
                                              behavior: "smooth",
                                            });
                                          } else if (
                                            window.location.pathname !== "/"
                                          ) {
                                            // Store section ID and navigate to homepage
                                            sessionStorage.setItem(
                                              "scrollToSection",
                                              sectionId
                                            );
                                            window.location.href = "/";
                                          }
                                        }
                                      }}
                                    >
                                      {dropItem.icon && (
                                        <i
                                          className={`${dropItem.icon} mr-[10px]`}
                                        ></i>
                                      )}
                                      {dropItem.title}
                                      {dropItem.subtitle && (
                                        <span className='block mt-2 md:mt-4 text-sm'>
                                          {dropItem.subtitle}
                                        </span>
                                      )}
                                    </Link>
                                  ))}
                                {dropItem.img && dropItem.link && (
                                  <Link
                                    to={dropItem.link}
                                    onClick={(e) => {
                                      e.preventDefault();
                                      if (dropItem.onClick) {
                                        dropItem.onClick();
                                      } else if (dropItem.scrollTo) {
                                        const sectionId = dropItem.scrollTo;
                                        // Scroll to section if on the same page
                                        const element =
                                          document.getElementById(sectionId);
                                        if (element) {
                                          element.scrollIntoView({
                                            behavior: "smooth",
                                          });
                                        } else if (
                                          window.location.pathname !== "/"
                                        ) {
                                          // Store section ID and navigate to homepage
                                          sessionStorage.setItem(
                                            "scrollToSection",
                                            sectionId
                                          );
                                          window.location.href = "/";
                                        }
                                      }
                                    }}
                                  >
                                    <img
                                      height='235'
                                      alt={dropItem.alt || "menu-banner"}
                                      width='210'
                                      className='inline-block max-w-[160px] rounded-sm'
                                      src={dropItem.img}
                                    />
                                  </Link>
                                )}
                              </li>
                            );
                          })}
                      </ul>
                    );
                  })}

                  {/* Image column - only show with default image if none is hovered and for services-megamenu */}
                  {item.megamenuClassName === "services-megamenu" && (
                    <div
                      className='specialty-image-container'
                      style={{
                        flex: "0 0 auto", // Prevent flex from shrinking this container
                        width: "180px",
                        height: "236px",

                        position: "relative",
                      }}
                    >
                      <img
                        src={currentHoverImage || defaultImage}
                        alt='Specialization'
                        style={{
                          display: "block",
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                          borderRadius: "2px",
                        }}
                      />
                    </div>
                  )}
                </div>
              )}{" "}
            </li>
          );
        })}
      </ul>
    </div>
  );
});
/* Menu Component End */
/* Mobile Menu Component Start */
export const MobileMenu = (props) => {
  const [activeMenu, setActiveMenu] = useState(null);
  const navigate = useNavigate();
  const { t } = useTranslation();
  const location = useLocation();

  // Detect current language from URL
  const currentLang = detectLanguageFromPath(location.pathname);
  const getLocalizedPath = (path) => {
    if (currentLang === DEFAULT_LANGUAGE) {
      return path;
    }
    return `/${currentLang}${path}`;
  };

  const toggleMenu = (index, e) => {
    e && e.preventDefault();
    setActiveMenu(activeMenu === index ? null : index);
  };

  return (
    <div className={`mobile-menu ${props.className || ""}`}>
      <LanguageSwitcher className='mobile-header-language-switcher d-lg-none' />
      <Navbar.Toggle
        id='toggle-bar'
        className='order-last md:ml-[17px] md:px-[15px]'
      >
        <span className='navbar-toggler-line'></span>
        <span className='navbar-toggler-line'></span>
        <span className='navbar-toggler-line'></span>
        <span className='navbar-toggler-line'></span>
      </Navbar.Toggle>
      <Navbar.Collapse
        id='myCollapsible'
        className='col-auto justify-center mobile-menu-full'
      >
        {/* Mobile Menu Header - Logo and Text */}
        <div
          className='mobile-menu-header'
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            width: "100%",
          }}
        >
          <div className='mobile-brand' style={{ marginLeft: "-1.8em" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "0px" }}>
              <svg
                className='mobile-logo-icon'
                width='78'
                height='70.2'
                viewBox='280 60 617 800'
                aria-labelledby='iq-logo-mobile-title'
                role='img'
              >
                <title
                  id='iq-logo-mobile-title'
                  style={{
                    fontFamily: "Oxanium",
                    fontWeight: 500,
                    color: "white",
                  }}
                >
                  {t('homepage:logoAlt', 'INAQ B2B Marketingová agentúra')}
                </title>
                <circle
                  cx='432.68'
                  cy='406.63'
                  r='46.86'
                  transform='translate(-38 768.59) rotate(-80.78)'
                  style={{ fill: "rgb(255, 255, 255)" }}
                ></circle>
                <path
                  d='M479.54,520.07v138.23c0,.48-.01.95-.03,1.42-.74,25.22-21.43,45.44-46.83,45.44s-46.09-20.22-46.83-45.44c-.02-.47-.03-.94-.03-1.42v-138.23c0-25.88,20.98-46.86,46.86-46.86s46.86,20.98,46.86,46.86Z'
                  style={{ fill: "rgb(255, 255, 255)" }}
                ></path>
                <path
                  d='M590.1,507.34c.55,14.01,15.99,25.25,34.95,25.25s34.4-11.24,34.95-25.25c0-.26.02-.52.02-.79v-63.26c-.19-19.29-.38-36.95-.57-56.25,0-5.97-3.84-12.55-11.63-17.98-5.52-3.85-11.66-4.89-11.66-4.89-.04-.01-.08-.01-.12-.02-6.65-1.28-12.18-2.1-17.32-2.61h-63.7c-.28,0-.56,0-.84-.02h-.16c-12.87-.53-23.13-11.12-23.13-24.11v-52.96c0-13.33,11.2-21.91,24.52-21.91h17.48c13.11,0,23.74,10.63,23.74,23.74v14.76c0,11.65,9.45,21.1,21.1,21.1h19.08c12.82,0,23.21-10.39,23.21-23.21v-16.59c0-6.87-.56-11.52-2.22-17.87-8.8-33.7-39.77-54.63-76.23-54.63h-40.31c-43.33,0-78.46,35.13-78.46,78.46v53.67c19.47,6.11,35.71,25.67,38.2,47.37l.15,1.6c1.46,11.01,13.69,19.55,25.06,19.79l7.32-.09c44.8.19,56.25-3.61,56.45,14.19l.12,82.51Z'
                  style={{ fill: "rgb(255, 255, 255)" }}
                ></path>
              </svg>
              <span className="mobile-brand-text font-['Oxanium'] text-white font-normal">
                {t('nav.mobileMenuSubtitle')}
              </span>
            </div>
          </div>
          <button
            className='mobile-close-btn text-white'
            aria-label="Close mobile menu"
            onClick={() => {
              document.body.classList.remove("navbar-collapse-show");
              const navbarCollapse = document.querySelector(".navbar-collapse");
              const toggleButton = document.querySelector(".navbar-toggler");
              if (navbarCollapse) navbarCollapse.classList.remove("show");
              if (toggleButton) {
                toggleButton.classList.add("collapsed");
                toggleButton.setAttribute("aria-expanded", "false");
              }
            }}
          >
            <svg
              width='24'
              height='24'
              viewBox='0 0 24 24'
              fill='none'
              xmlns='http://www.w3.org/2000/svg'
              style={{ transform: "translateX(2px) translateY(2px)" }}
            >
              <path
                d='M18 6L6 18'
                stroke='currentColor'
                strokeWidth='2'
                strokeLinecap='round'
                strokeLinejoin='round'
              />
              <path
                d='M6 6L18 18'
                stroke='currentColor'
                strokeWidth='2'
                strokeLinecap='round'
                strokeLinejoin='round'
              />
            </svg>
          </button>
        </div>

        <ReactCustomScrollbar
          className='mobile-menu-content pr-[0px]'
          theme='light'
          autoHide
        >
          <div>
            <ul className='navbar-nav'>
              {props.data.map((item, i) => {
                // Special handling for the Služby item
                if (item.id === "sluzby-menu-item") {
                  return (
                    <li
                      key={i}
                      className={`nav-item dropdown ${activeMenu === i ? "open" : ""}`}
                    >
                      {/* Main menu item */}
                      <div className='mobile-menu-item-wrapper'>
                        <span
                          className={`nav-link relative ${item.id}`}
                          onClick={(e) => {
                            if (item.onClick) {
                              e.preventDefault();
                              item.onClick();
                            }
                          }}
                        >
                          {item.title}
                        </span>

                        {/* SVG Toggle Arrow */}
                        <button
                          className='mobile-toggle-button'
                          onClick={(e) => toggleMenu(i, e)}
                          aria-label='Toggle submenu'
                        >
                          <svg
                            className={`w-6 h-6 transition-transform ${activeMenu === i ? "rotate-180" : ""}`}
                            fill='none'
                            stroke='currentColor'
                            viewBox='0 0 24 24'
                            xmlns='http://www.w3.org/2000/svg'
                          >
                            <path
                              strokeLinecap='round'
                              strokeLinejoin='round'
                              strokeWidth='3'
                              d='M19 9l-7 7-7-7'
                            ></path>
                          </svg>
                        </button>
                      </div>

                      {/* Dropdown content */}
                      {activeMenu === i && (
                        <div className='mobile-submenu py-2'>
                          {/* Display all megamenu sections */}
                          {item.megamenu.map((section, j) => {
                            return (
                              <div key={j}>
                                <h6 className='submenu-title font-["Oxanium"] text-[15px] px-4 pt-3 mb-2 text-white/90 font-[500]'>
                                  {section.title}
                                </h6>
                                <ul className='pl-0'>
                                  {section.dropdown.map((subItem, k) => (
                                      <li
                                        key={k}
                                        className='mobile-menu-item py-2 pl-6'
                                      >
                                        {subItem.link &&
                                        !subItem.modalAction ? (
                                          <Link
                                            to={subItem.link}
                                            className='nav-link mobile-menu-nav-link'
                                            onClick={(e) => {
                                              if (subItem.onClick) {
                                                e.preventDefault();
                                                subItem.onClick();

                                                // Close mobile menu
                                                document.body.classList.remove(
                                                  "navbar-collapse-show"
                                                );
                                              } else if (subItem.scrollTo) {
                                                e.preventDefault();
                                                const sectionId = subItem.scrollTo;
                                                // Check if already on homepage (considering language prefix)
                                                const homePath = getLocalizedPath("/");
                                                if (window.location.pathname === "/" || window.location.pathname === "/en" || window.location.pathname === "/en/") {
                                                  // Scroll directly if on homepage
                                                  const element = document.getElementById(sectionId);
                                                  if (element) {
                                                    element.scrollIntoView({
                                                      behavior: "smooth",
                                                    });
                                                  }
                                                } else {
                                                  // Store section ID and use navigate with localized path
                                                  sessionStorage.setItem(
                                                    "scrollToSection",
                                                    sectionId
                                                  );
                                                  navigate(homePath); // Use localized homepage path
                                                }

                                                // Close mobile menu
                                                document.body.classList.remove(
                                                  "navbar-collapse-show"
                                                );
                                              }
                                            }}
                                          >
                                            {subItem.title}
                                          </Link>
                                        ) : subItem.modalAction ? (
                                          <span
                                            className='nav-link cursor-pointer your-segment'
                                            onClick={() => {
                                              if (props.openModal) {
                                                props.openModal();

                                                // Close mobile menu
                                                document.body.classList.remove(
                                                  "navbar-collapse-show"
                                                );
                                              }
                                            }}
                                          >
                                            {subItem.title}
                                          </span>
                                        ) : (
                                          <span className='nav-link text-[15px]'>
                                            {subItem.title}
                                          </span>
                                        )}
                                      </li>
                                    ))}
                                  </ul>
                                </div>
                              );
                          })}
                        </div>
                      )}
                    </li>
                  );
                }

                // Regular menu items
                return (
                  <li key={i} className='nav-item'>
                    <div className='mobile-menu-item-wrapper'>
                      {item.link ? (
                        item.external ? (
                          <a
                            className={`nav-link relative ${item.id ? item.id : ""}`}
                            href={item.link}
                            target='_blank'
                            rel='noopener noreferrer'
                            onClick={() => {
                              document.body.classList.remove(
                                "navbar-collapse-show"
                              );
                              const navbarCollapse =
                                document.querySelector(".navbar-collapse");
                              const toggleButton =
                                document.querySelector(".navbar-toggler");
                              if (navbarCollapse)
                                navbarCollapse.classList.remove("show");
                              if (toggleButton) {
                                toggleButton.classList.add("collapsed");
                                toggleButton.setAttribute(
                                  "aria-expanded",
                                  "false"
                                );
                              }
                            }}
                          >
                            {item.title}
                          </a>
                        ) : (
                          <Link
                            className={`nav-link relative ${item.id ? item.id : ""}`}
                            to={item.link}
                          onClick={(e) => {
                            console.log(
                              `Mobile menu item clicked: ${item.title}`
                            );

                            if (item.onClick) {
                              e.preventDefault();
                              item.onClick();
                            } else if (item.scrollTo) {
                              e.preventDefault();
                              const sectionId = item.scrollTo;
                              console.log(
                                `Trying to scroll to ${sectionId} from main menu item`
                              );

                              // Check if already on homepage (considering language prefix)
                              const homePath = getLocalizedPath("/");
                              if (window.location.pathname === "/" || window.location.pathname === "/en" || window.location.pathname === "/en/") {
                                console.log(
                                  "Already on homepage, scrolling directly"
                                );
                                const element =
                                  document.getElementById(sectionId);
                                if (element) {
                                  console.log(
                                    `Element with ID ${sectionId} found, scrolling...`
                                  );
                                  element.scrollIntoView({
                                    behavior: "smooth",
                                  });
                                } else {
                                  console.error(
                                    `Element with ID ${sectionId} NOT FOUND`
                                  );
                                }
                              } else {
                                console.log(
                                  `Navigating to homepage with target section: ${sectionId}`
                                );
                                // Store section ID and use navigate with localized path
                                sessionStorage.setItem(
                                  "scrollToSection",
                                  sectionId
                                );
                                navigate(homePath); // Use localized homepage path
                              }
                            }

                            // Close mobile menu
                            document.body.classList.remove(
                              "navbar-collapse-show"
                            );
                            const navbarCollapse =
                              document.querySelector(".navbar-collapse");
                            const toggleButton =
                              document.querySelector(".navbar-toggler");
                            if (navbarCollapse)
                              navbarCollapse.classList.remove("show");
                            if (toggleButton) {
                              toggleButton.classList.add("collapsed");
                              toggleButton.setAttribute(
                                "aria-expanded",
                                "false"
                              );
                            }
                          }}
                        >
                          {item.title}
                          {item.notification && (
                            <span
                              className={`notification-dot absolute top-[-8px] right-[-15px] bg-red-500 text-white rounded-full w-[16px] h-[16px] flex font-rubik items-center justify-center text-[10px] ${
                                item.notification.animate ? "" : "no-animate"
                              }`}
                            >
                              {parseInt(item.notification.count, 10) || 2}
                            </span>
                          )}
                        </Link>
                        )
                      ) : (
                        <span
                          className={`nav-link relative ${item.id ? item.id : ""}`}
                        >
                          {item.title}
                        </span>
                      )}

                      {/* Toggle button for other dropdown items */}
                      {(item.dropdown || item.megamenu) && (
                        <button
                          className='mobile-toggle-button'
                          onClick={(e) => toggleMenu(i, e)}
                          aria-label='Toggle submenu'
                        >
                          <svg
                            className={`w-6 h-6 transition-transform ${activeMenu === i ? "rotate-180" : ""}`}
                            fill='none'
                            stroke='currentColor'
                            viewBox='0 0 24 24'
                            xmlns='http://www.w3.org/2000/svg'
                          >
                            <path
                              strokeLinecap='round'
                              strokeLinejoin='round'
                              strokeWidth='3'
                              d='M19 9l-7 7-7-7'
                            ></path>
                          </svg>
                        </button>
                      )}
                    </div>

                    {/* Dropdown for other menu items */}
                    {activeMenu === i && (item.dropdown || item.megamenu) && (
                      <div className='mobile-submenu py-3'>
                        {/* Display regular items */}
                        <ul className='pl-0'>
                          {item.dropdown
                            ? item.dropdown.map((subItem, k) => (
                                <li
                                  key={k}
                                  className='mobile-menu-item py-2 pl-6'
                                >
                                  <Link
                                    to={subItem.link || "#"}
                                    className='nav-link text-[15px]'
                                    onClick={(e) => {
                                      if (subItem.onClick) {
                                        e.preventDefault();
                                        subItem.onClick();
                                      } else if (subItem.scrollTo) {
                                        e.preventDefault();
                                        const sectionId = subItem.scrollTo;
                                        // Check if already on homepage (considering language prefix)
                                        const homePath = getLocalizedPath("/");
                                        if (window.location.pathname === "/" || window.location.pathname === "/en" || window.location.pathname === "/en/") {
                                          // Scroll directly if on homepage
                                          const element =
                                            document.getElementById(sectionId);
                                          if (element) {
                                            element.scrollIntoView({
                                              behavior: "smooth",
                                            });
                                          }
                                        } else {
                                          // Store section ID and use navigate with localized path
                                          sessionStorage.setItem(
                                            "scrollToSection",
                                            sectionId
                                          );
                                          navigate(homePath); // Use localized homepage path
                                        }
                                      }

                                      // Close mobile menu
                                      document.body.classList.remove(
                                        "navbar-collapse-show"
                                      );
                                    }}
                                  >
                                    {subItem.title}
                                  </Link>
                                </li>
                              ))
                            : item.megamenu &&
                              item.megamenu[0].dropdown.map((subItem, k) => (
                                <li
                                  key={k}
                                  className='mobile-menu-item py-2 pl-6'
                                >
                                  <Link
                                    to={subItem.link || "#"}
                                    className='nav-link text-[15px]'
                                    onClick={() => {
                                      // Close mobile menu
                                      document.body.classList.remove(
                                        "navbar-collapse-show"
                                      );
                                    }}
                                  >
                                    {subItem.title}
                                  </Link>
                                </li>
                              ))}
                        </ul>
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          </div>
        </ReactCustomScrollbar>

        {/* Mobile Menu Footer - CTA Button and Social Icons */}
        <div className='mobile-menu-footer'>
          <button
            onClick={() => {
              if (props.openModal) {
                props.openModal();

                // Close mobile menu completely - make this more aggressive
                document.body.classList.remove("navbar-collapse-show");
                const navbarCollapse =
                  document.querySelector(".navbar-collapse");
                const toggleButton = document.querySelector(".navbar-toggler");

                if (navbarCollapse) {
                  navbarCollapse.classList.remove("show");
                }

                if (toggleButton) {
                  toggleButton.classList.add("collapsed");
                  toggleButton.setAttribute("aria-expanded", "false");
                }
              }
            }}
            className='mobile-cta-button'
          >
            {t('buttons.nonCommittalInquiry')}
          </button>

          <div className='mobile-social-icons'>
            <a
              href='https://www.facebook.com/inaq.sk'
              target='_blank'
              rel='noopener noreferrer'
              className='mobile-social-icon'
              aria-label="Visit our Facebook page"
            >
              <svg
                xmlns='http://www.w3.org/2000/svg'
                width='24'
                height='24'
                viewBox='0 0 24 24'
                fill='currentColor'
                aria-hidden="true"
              >
                <path d='M9.19795 21.5H13.198V13.4901H16.8021L17.198 9.50977H13.198V7.5C13.198 6.94772 13.6457 6.5 14.198 6.5H17.198V2.5H14.198C11.4365 2.5 9.19795 4.73858 9.19795 7.5V9.50977H7.19795L6.80206 13.4901H9.19795V21.5Z' />
              </svg>
            </a>
            <a
              href='https://www.instagram.com/inaq.sk'
              target='_blank'
              rel='noopener noreferrer'
              className='mobile-social-icon'
              aria-label="Visit our Instagram page"
            >
              <svg
                xmlns='http://www.w3.org/2000/svg'
                width='24'
                height='24'
                viewBox='0 0 24 24'
                fill='currentColor'
                aria-hidden="true"
              >
                <path d='M12 2C14.717 2 15.056 2.01 16.122 2.06C17.187 2.11 17.912 2.277 18.55 2.525C19.21 2.779 19.766 3.123 20.322 3.678C20.8305 4.1779 21.224 4.78259 21.475 5.45C21.722 6.087 21.89 6.813 21.94 7.878C21.987 8.944 22 9.283 22 12C22 14.717 21.99 15.056 21.94 16.122C21.89 17.187 21.722 17.912 21.475 18.55C21.2247 19.2178 20.8311 19.8226 20.322 20.322C19.822 20.8303 19.2173 21.2238 18.55 21.475C17.913 21.722 17.187 21.89 16.122 21.94C15.056 21.987 14.717 22 12 22C9.283 22 8.944 21.99 7.878 21.94C6.813 21.89 6.088 21.722 5.45 21.475C4.78233 21.2245 4.17753 20.8309 3.678 20.322C3.16941 19.8222 2.77593 19.2175 2.525 18.55C2.277 17.913 2.11 17.187 2.06 16.122C2.013 15.056 2 14.717 2 12C2 9.283 2.01 8.944 2.06 7.878C2.11 6.812 2.277 6.088 2.525 5.45C2.77524 4.78218 3.1688 4.17732 3.678 3.678C4.17767 3.16923 4.78243 2.77573 5.45 2.525C6.088 2.277 6.812 2.11 7.878 2.06C8.944 2.013 9.283 2 12 2ZM12 7C10.6739 7 9.40215 7.52678 8.46447 8.46447C7.52678 9.40215 7 10.6739 7 12C7 13.3261 7.52678 14.5979 8.46447 15.5355C9.40215 16.4732 10.6739 17 12 17C13.3261 17 14.5979 16.4732 15.5355 15.5355C16.4732 14.5979 17 13.3261 17 12C17 10.6739 16.4732 9.40215 15.5355 8.46447C14.5979 7.52678 13.3261 7 12 7ZM18.5 6.75C18.5 6.41848 18.3683 6.10054 18.1339 5.86612C17.8995 5.6317 17.5815 5.5 17.25 5.5C16.9185 5.5 16.6005 5.6317 16.3661 5.86612C16.1317 6.10054 16 6.41848 16 6.75C16 7.08152 16.1317 7.39946 16.3661 7.63388C16.6005 7.8683 16.9185 8 17.25 8C17.5815 8 17.8995 7.8683 18.1339 7.63388C18.3683 7.39946 18.5 7.08152 18.5 6.75ZM12 9C12.7956 9 13.5587 9.31607 14.1213 9.87868C14.6839 10.4413 15 11.2044 15 12C15 12.7956 14.6839 13.5587 14.1213 14.1213C13.5587 14.6839 12.7956 15 12 15C11.2044 15 10.4413 14.6839 9.87868 14.1213C9.31607 13.5587 9 12.7956 9 12C9 11.2044 9.31607 10.4413 9.87868 9.87868C10.4413 9.31607 11.2044 9 12 9Z' />
              </svg>
            </a>
          </div>
        </div>
      </Navbar.Collapse>
    </div>
  );
};
/* HamburgerMenu Component Start */
export const HamburgerMenu = memo((props) => {
  const ref = useRef(null);
  const [show, setShow] = useState(false);
  useOnClickOutside(ref, (e) => setShow(false));

  useEffect(() => {
    const close = (e) => {
      if (e.keyCode === 27) {
        setShow(false);
      }
    };

    if (show === true) {
      document.querySelector("body").classList.add("overflow-hidden");
      document.querySelector(".push-button").classList.remove("collapsed");
    } else {
      document.querySelector("body").classList.remove("overflow-hidden");
      document.querySelector(".push-button").classList.add("collapsed");
    }

    window.addEventListener("keydown", close);
    return () => window.removeEventListener("keydown", close);
  }, [show]);

  return (
    <>
      {[false].map((expand) => (
        <Navbar
          key={expand}
          expand={expand}
          className={`header-push-button bg-transparent inline-block ${props.theme}`}
        >
          <Navbar.Toggle
            className={`push-button`}
            onClick={() => setShow(true)}
          >
            <div className='nav-icon'>
              <span className='navbar-toggler-line'></span>
              <span className='navbar-toggler-line'></span>
              <span className='navbar-toggler-line'></span>
              <span className='navbar-toggler-line'></span>
            </div>
          </Navbar.Toggle>
          <div
            className={`${
              show
                ? "block h-[100vh] left-0 overflow-hidden fixed top-0 w-full z-[999]"
                : ""
            }`}
          >
            <div
              ref={ref}
              className={`hamburger-menu-wrapper pos-${props.position}${
                show ? " show" : ""
              }${props.className ? ` ${props.className}` : ""}`}
            >
              {props.closeBtn && (
                <button
                  aria-label='hamburger menu close button'
                  className='close-btn'
                  onClick={() => setShow(false)}
                >
                  <i className='fas fa-times'></i>
                </button>
              )}
              {show && props.children}
            </div>
          </div>
        </Navbar>
      ))}
    </>
  );
});
/* HamburgerMenu Component End */

/* HeaderLanguage Component Start */
export const HeaderLanguage = (props) => {
  return (
    <div
      className={`header-language dropdown inline-block align-middle pl-[17px] text-[17px]${
        props.className ? ` ${props.className}` : ""
      }`}
      style={props.style}
    >
      <Link to='#' aria-label='language' onClick={(e) => e.preventDefault()}>
        <i
          className={`feather-globe py-[30px] px-0 inline-block ${props.className}`}
        ></i>
      </Link>
      <ul className='dropdown-menu block absolute font-serif left-auto right-0 p-15px rounded-[6px] border-0 m-0 min-w-[140px]'>
        <li>
          <Link
            aria-label='link'
            onClick={(e) => e.preventDefault()}
            to='#'
            title='English'
          >
            <span className='icon-country block py-[2px] px-0 text-xs text-[#828282]'>
              <img
                src='/assets/img/webp/usa.webp'
                alt='usa'
                width='16'
                height='16'
              />
            </span>
            English
          </Link>
        </li>
        <li>
          <Link
            aria-label='link'
            onClick={(e) => e.preventDefault()}
            to='#'
            title='England'
          >
            <span className='icon-country block py-[2px] px-0 text-xs text-[#828282]'>
              <img
                src='/assets/img/webp/england.webp'
                alt='england'
                width='16'
                height='16'
              />
            </span>
            England
          </Link>
        </li>
        <li>
          <Link
            aria-label='link'
            onClick={(e) => e.preventDefault()}
            to='#'
            title='France'
          >
            <span className='icon-country block py-[2px] px-0 text-xs text-[#828282]'>
              <img
                src='/assets/img/webp/france.webp'
                alt='france'
                width='16'
                height='16'
              />
            </span>
            France
          </Link>
        </li>
        <li>
          <Link
            aria-label='link'
            onClick={(e) => e.preventDefault()}
            to='#'
            title='Russian'
          >
            <span className='icon-country block py-[2px] px-0 text-xs text-[#828282]'>
              <img
                src='/assets/img/webp/russian.webp'
                alt='russian'
                width='16'
                height='16'
              />
            </span>
            Russian
          </Link>
        </li>
        <li>
          <Link
            aria-label='link'
            onClick={(e) => e.preventDefault()}
            to='#'
            title='Spain'
          >
            <span className='icon-country block py-[2px] px-0 text-xs text-[#828282]'>
              <img
                src='/assets/img/webp/spain.webp'
                alt='spain'
                width='16'
                height='16'
              />
            </span>
            Spain
          </Link>
        </li>
      </ul>
    </div>
  );
};
/* HeaderLanguage Component End */

Header.defaultProps = {
  topSpace: {
    desktop: false,
  },
};

Header.propTypes = {
  type: PropTypes.string,
  topSpace: PropTypes.object,
};

HeaderNav.defaultProps = {
  fluid: "lg",
  theme: "dark",
  menu: "light",
  expand: "lg",
};

HeaderNav.propTypes = {
  fluid: PropTypes.string,
  theme: PropTypes.string,
  bg: PropTypes.string,
  className: PropTypes.string,
};

HamburgerMenu.defaultProps = {
  theme: "light",
  position: "right",
  closeBtn: true,
};

HamburgerMenu.propTypes = {
  theme: PropTypes.string,
  position: PropTypes.string,
  closeBtn: PropTypes.bool,
};

Menu.defaultProps = {
  data: HeaderData,
};

MobileMenu.defaultProps = {
  type: "full",
  data: [],
};

MobileMenu.propTypes = {
  type: PropTypes.string,
};

export default Header;
